import {
    handleRemoveDocumentProperties,
    handleSetDocumentProperties,
    handleSetSubjectProperties,
    handleRemoveSubjectProperties,
    handleUpdateDescription,
    handleUpdateElementsLabel,
    handleUpdateNetsLabel,
} from "@buildwithflux/core";
import {Records} from "@buildwithflux/shared";
import {isEmpty} from "lodash";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {applyRemoveAssets, applySetAssets} from "../actions/assetActions";
import {applyTimestampUpdate} from "../actions/documentPropertyActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type PropertySliceState = Pick<
    DocumentStoreState,
    | "setAssets"
    | "removeAssets"
    | "setDocumentProperties"
    | "removeDocumentProperties"
    | "updateDescription"
    | "updateName"
    | "setSubjectProperties"
    | "removeSubjectProperties"
    | "updateNetsLabel"
    | "updateElementsLabel"
>;

export function createPropertySlice(deps: SliceDependencies): PropertySliceState {
    const {primitives, get} = deps;

    return {
        setAssets: (assets) => {
            if (Records.isEmpty(assets)) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetAssets(draft, assets), {
                actionType: "setAssets",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        removeAssets: (assetUids) => {
            if (assetUids.length === 0) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyRemoveAssets(draft, assetUids), {
                actionType: "removeAssets",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        setDocumentProperties: (setProperties) => {
            if (!setProperties || isEmpty(setProperties)) {
                return;
            }
            const document = get().document;
            if (!document?.properties) {
                return;
            }
            performDocumentMutation(primitives, (draft) => handleSetDocumentProperties(draft, setProperties), {
                actionType: "setDocumentProperties",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        removeDocumentProperties: (propertyUids) => {
            if (!propertyUids || propertyUids.length === 0) {
                return;
            }
            const document = get().document;
            if (!document?.properties) {
                return;
            }
            performDocumentMutation(primitives, (draft) => handleRemoveDocumentProperties(draft, propertyUids), {
                actionType: "removeDocumentProperties",
                canUndo: true,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
            });
        },

        updateDescription: (description) => {
            if (description === undefined) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    handleUpdateDescription(draft, description);
                    applyTimestampUpdate(draft, true, primitives.getCurrentUser());
                },
                {
                    actionType: "updateDescription",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        updateName: (newDocumentName, newDocumentSlug) => {
            if (!newDocumentName || !newDocumentSlug) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    draft.name = newDocumentName;
                    draft.slug = newDocumentSlug;
                },
                {
                    actionType: "updateName",
                    canUndo: false,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        setSubjectProperties: (propertyUpdateMap, subscriptionKey) => {
            if (!propertyUpdateMap || isEmpty(propertyUpdateMap)) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => {
                    try {
                        handleSetSubjectProperties(draft, FluxLogger, propertyUpdateMap, subscriptionKey);
                    } catch (e) {
                        FluxLogger.captureError(e);
                    }
                },
                {
                    actionType: "setSubjectProperties",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                    additionalPayload: subscriptionKey ? {subscriptionKey} : undefined,
                },
            );
        },

        removeSubjectProperties: (propertyRemovalMap) => {
            if (!propertyRemovalMap || isEmpty(propertyRemovalMap)) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => handleRemoveSubjectProperties(draft, propertyRemovalMap, FluxLogger),
                {
                    actionType: "removeSubjectProperties",
                    canUndo: true,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: true,
                },
            );
        },

        updateNetsLabel: (netLabelMap, canUndo = true) => {
            if (!netLabelMap || isEmpty(netLabelMap)) {
                return;
            }
            performDocumentMutation(primitives, (draft) => handleUpdateNetsLabel(draft, netLabelMap), {
                actionType: "updateNetsLabel",
                canUndo,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: true,
                additionalPayload: {
                    hasInheritedRules: true,
                    hasInheritedCalculations: true,
                    requiresDescendantRebake: true,
                    requiresNetDescendantRebake: false,
                },
            });
        },

        updateElementsLabel: (elements, canUndo = true) => {
            if (!elements || isEmpty(elements)) {
                return;
            }
            performDocumentMutation(primitives, (draft) => handleUpdateElementsLabel(draft, elements, FluxLogger), {
                actionType: "updateElementsLabel",
                canUndo,
                shouldGenerateActionRecord: canUndo,
                updatesDocumentTimestamp: canUndo,
            });
        },
    };
}
